/**
 * Invite many: upload a spreadsheet of staff, see who will be invited and
 * which lines need fixing, take anyone off the list, send, and see what
 * happened to each row. The sample file is written against the
 * organization's own departments, so it imports clean.
 */

import { useMemo, useState, type ChangeEvent } from 'react';
import { ROLE_DISPLAY_NAMES } from '../../types/roles';
import { fullName } from '../../types/personName';
import { isCallFailure } from '../../lib/callable';
import { useInviteUsersBulk, type BulkInviteRowResult } from './useMembers';
import { parseStaffCsv, staffCsvSample, STAFF_CSV_HEADER } from './parseStaffCsv';
import type { TaxonomyItem } from './useTaxonomy';
import { CopyLink } from './CopyLink';
import styles from '../../shared/Page.module.css';
import own from './Admin.module.css';

const MAX_ROWS = 200;
export const SAMPLE_FILE_NAME = 'staff-invite-sample.csv';

function outcome(r: BulkInviteRowResult): string {
  if (r.status === 'already_member') return 'Already a member';
  if (r.status === 'already_invited') return 'Already invited';
  if (r.status === 'failed') return `Not invited: ${r.reason}`;
  return r.emailSent ? 'Invited, email sent' : `Invited, but the email could not be sent${r.emailReason ? ` (${r.emailReason})` : ''}`;
}

export function BulkInvite({ orgId, departments }: { orgId: string | null; departments: TaxonomyItem[] }) {
  const send = useInviteUsersBulk(orgId);
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const parsed = useMemo(() => parseStaffCsv(text, departments), [text, departments]);
  const deptName = (id: string) => departments.find((d) => d.id === id)?.name ?? id;
  const tooMany = parsed.rows.length > MAX_ROWS;
  const sampleHref = useMemo(() => `data:text/csv;charset=utf-8,${encodeURIComponent(staffCsvSample(departments))}`, [departments]);

  function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { setText(typeof reader.result === 'string' ? reader.result : ''); setFileName(file.name); send.reset(); };
    reader.readAsText(file);
    // So choosing the same file again after clearing still fires.
    e.target.value = '';
  }

  function clear() { setText(''); setFileName(null); send.reset(); }

  /**
   * Take one person out of the list before anything is sent. The rows are
   * the file's lines, so this drops their line; no confirmation, because
   * nothing exists yet to lose.
   */
  function dropLine(line: number) {
    setText((t) => t.split(/\r?\n/).filter((_, i) => i !== line - 1).join('\n'));
    send.reset();
  }

  return (
    <div>
      <p className={styles.itemMeta} style={{ marginBottom: 'var(--sp-3)' }}>
        A CSV with the columns <code>{STAFF_CSV_HEADER}</code>, one person per line. A single <code>name</code> column is accepted too. Several roles or
        departments in one cell, separated by semicolons. Roles are Viewer, Author, Reviewer, Approver,
        Administrator; departments by name as they appear under Taxonomy. Up to {MAX_ROWS} people at a time.
      </p>
      <div style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'center', flexWrap: 'wrap', marginBottom: 'var(--sp-3)' }}>
        <label className={`${styles.btn} ${styles.btnPrimary}`} style={{ cursor: 'pointer' }}>
          Choose a CSV file
          <input type="file" accept=".csv,text/csv,text/plain" onChange={onFile} className="vh" aria-label="Choose a CSV file" />
        </label>
        <a className={styles.btn} href={sampleHref} download={SAMPLE_FILE_NAME} style={{ textDecoration: 'none' }}>
          Download the sample file
        </a>
        {fileName && (
          <span className={styles.itemMeta}>
            <strong>{fileName}</strong>
            {' · '}
            <button type="button" className={styles.linkBtn} onClick={clear}>Clear</button>
          </span>
        )}
      </div>

      {parsed.errors.length > 0 && (
        <div className={`${styles.notice} ${styles.noticeWarn}`} role="alert" style={{ marginTop: 'var(--sp-3)' }}>
          <strong>{parsed.errors.length} {parsed.errors.length === 1 ? 'line needs' : 'lines need'} fixing</strong> in the file and will not be sent:
          <ul style={{ margin: 'var(--sp-2) 0 0', paddingLeft: '1.2em' }}>
            {parsed.errors.map((e) => <li key={e.line}>Line {e.line}: {e.reason}</li>)}
          </ul>
        </div>
      )}

      {fileName && parsed.rows.length === 0 && parsed.errors.length === 0 && (
        <p className={styles.state}>Nobody to invite in that file. Check it has the header line and at least one person.</p>
      )}

      {parsed.rows.length > 0 && !send.data && (
        <div className={own.scroller} style={{ marginTop: 'var(--sp-3)' }}>
          <table className={own.table}>
            <caption>{parsed.rows.length} {parsed.rows.length === 1 ? 'person' : 'people'} to invite</caption>
            <thead><tr><th scope="col">Email</th><th scope="col">Name</th><th scope="col">Roles</th><th scope="col">Departments</th><th scope="col">Title</th><th scope="col"><span className="vh">Remove</span></th></tr></thead>
            <tbody>
              {parsed.rows.map((r) => (
                <tr key={r.email}>
                  <td>{r.email}</td>
                  <td>{fullName(r.firstName, r.lastName) || <span style={{ color: 'var(--faint)' }}>—</span>}</td>
                  <td>{r.roles.map((x) => ROLE_DISPLAY_NAMES[x]).join(', ')}</td>
                  <td>{(r.departmentIds ?? []).map(deptName).join(', ') || <span style={{ color: 'var(--faint)' }}>—</span>}</td>
                  <td>{r.title ?? <span style={{ color: 'var(--faint)' }}>—</span>}</td>
                  <td>
                    <button type="button" className={own.trashBtn} aria-label={`Remove ${r.email} from the list`} title="Remove from the list" onClick={() => dropLine(r.line)}>
                      <svg width="16" height="16" viewBox="0 0 20 20" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3.5 5.5h13M8 5.5V4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1.5M5 5.5l.8 10.2a1.5 1.5 0 0 0 1.5 1.3h5.4a1.5 1.5 0 0 0 1.5-1.3L15 5.5M8.3 9v5M11.7 9v5" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {send.error && (
        <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert" style={{ marginTop: 'var(--sp-3)' }}>
          {isCallFailure(send.error) ? send.error.message : 'The invitations could not be sent.'}
        </div>
      )}

      {send.data && (
        <div style={{ marginTop: 'var(--sp-3)' }}>
          <div className={`${styles.notice} ${send.data.emailFailures ? styles.noticeWarn : styles.noticeInfo}`} role="status">
            {send.data.invited} invited{send.data.emailFailures ? `, ${send.data.emailFailures} whose email could not be sent — copy their links below` : ''}.
            {send.data.results.length - send.data.invited > 0 && ` ${send.data.results.length - send.data.invited} skipped as already here or already invited.`}
          </div>
          <div className={own.scroller}>
            <table className={own.table}>
              <caption>What happened to each row</caption>
              <thead><tr><th scope="col">Email</th><th scope="col">Outcome</th><th scope="col"><span className="vh">Actions</span></th></tr></thead>
              <tbody>
                {send.data.results.map((r) => (
                  <tr key={r.email}>
                    <td>{r.email}</td>
                    <td>{outcome(r)}</td>
                    <td>{r.status === 'invited' && !r.emailSent && r.inviteLink && <CopyLink link={r.inviteLink} label="Copy link" />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" className={styles.btn} style={{ marginTop: 'var(--sp-3)' }} onClick={clear}>Upload another file</button>
        </div>
      )}

      {!send.data && parsed.rows.length > 0 && (
        <div style={{ marginTop: 'var(--sp-3)' }}>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnPrimary}`}
            disabled={tooMany || send.isPending}
            onClick={() => send.mutate({ rows: parsed.rows.map(({ line: _line, ...row }) => row) })}
          >
            {send.isPending ? 'Sending…' : `Invite ${parsed.rows.length} ${parsed.rows.length === 1 ? 'person' : 'people'}`}
          </button>
          {tooMany && <p className={own.fieldError}>At most {MAX_ROWS} at a time. Split the list.</p>}
        </div>
      )}
    </div>
  );
}
